import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import Login from './components/Login';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import Orders from './pages/Orders';
import Users from './pages/Users';
import UserDetails from './pages/UserDetails';
import OrderDetails from './pages/OrderDetails';
import Admin from './pages/Admin';
import AdminPromoCodeDetails from './pages/AdminPromoCodeDetails';
import AdminPushForm from './pages/AdminPushForm';
import Complaints from './pages/Complaints'; 
import ComplaintDetails from './pages/ComplaintDetails';
import Raffle from './pages/Raffle';
import StatisticsPage from './statistics/StatisticsPage';
import ChatPage from './chat/ChatPage';
import CalendarPage from './calendar/CalendarPage';
import { ChatProvider } from './chat/ChatContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import { login, isTokenValid, logout, ALLOWED_ROLES, checkAccess } from './api';
import './css/styles.css';
import './css/sidebar.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ScrollToTopOnRouteChange = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  return null;
};

/* Страница розыгрыша теперь часть админки: требуется логин и роль ADMIN. */
const RAFFLE_PUBLIC_ACCESS = false;

const AppContent = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isRaffleRoute = location.pathname.replace(/\/+$/, '') === '/raffle';
  const publicRaffle = RAFFLE_PUBLIC_ACCESS && isRaffleRoute;
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Выдвижная панель на мобильных и свёрнутый режим на десктопе.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('sidebarCollapsed') === '1'
  );

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed ? '1' : '0');
  }, [sidebarCollapsed]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Проверка аутентификации - УПРОЩЕННАЯ ВЕРСИЯ (MOCKED FOR REDESIGN)
  const verifyAuthentication = async () => {
    try {
      const isValid = await isTokenValid();
      
      if (isValid) {
        // Пытаемся получить данные пользователя из localStorage (в реальном приложении - из API)
        const savedUserStr = localStorage.getItem('user');
        if (savedUserStr) {
          const savedUser = JSON.parse(savedUserStr);
          setUser(savedUser);
          setIsLoggedIn(true);
        } else {
          // Если токен валиден, но нет пользователя - вызываем logout для очистки стейта
          handleLogout();
        }
      } else {
        handleLogout();
      }
    } catch (err) {
      console.error('Auth verification failed:', err);
      handleLogout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    verifyAuthentication();
  }, []);

  // Тёмная тема доступна всем: и залогиненным (тумблер в навбаре), и на
  // экране входа (тумблер в Login). Здесь применяем сохранённый выбор из
  // localStorage, пока пользователь не вошёл; после входа тему ведёт навбар.
  useEffect(() => {
    if (!isLoggedIn) {
      const saved = localStorage.getItem('theme') === 'dark' ? 'dark' : 'light';
      document.documentElement.setAttribute('data-theme', saved);
    }
  }, [isLoggedIn, user]);

  const handleLogin = async (phoneNumber, password, role) => {
    try {
      setError(null);
      const data = await login(phoneNumber, password, role);

      if (data.response === 'Success' && data.accessToken && data.user) {
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        localStorage.setItem('user', JSON.stringify(data.user));

        setUser(data.user);
        setIsLoggedIn(true);
        setError(null);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setIsLoggedIn(false);
  };

  const getRequiredRoles = (path) => {
    switch (path) {
      case '/admin':
        return [ALLOWED_ROLES.MODERATOR, ALLOWED_ROLES.ADMIN, ALLOWED_ROLES.DEV];
      case '/admin/notifications':
      case '/admin/promo-codes/:promoId':
        return [ALLOWED_ROLES.ADMIN, ALLOWED_ROLES.DEV];
      case '/statistics':
        return [ALLOWED_ROLES.ADMIN, ALLOWED_ROLES.DEV];
      case '/calendar':
      case '/chat':
        return [ALLOWED_ROLES.SUPPORT, ALLOWED_ROLES.MODERATOR, ALLOWED_ROLES.ADMIN, ALLOWED_ROLES.DEV];
      case '/users':
      case '/users/:userId':
        return [ALLOWED_ROLES.SUPPORT, ALLOWED_ROLES.MODERATOR, ALLOWED_ROLES.ADMIN, ALLOWED_ROLES.DEV];
      case '/orders':
      case '/orders/:orderId':
        return [ALLOWED_ROLES.SUPPORT, ALLOWED_ROLES.MODERATOR, ALLOWED_ROLES.ADMIN, ALLOWED_ROLES.DEV];
      case '/complaints':
      case '/complaints/:complaintId':
        return [ALLOWED_ROLES.SUPPORT, ALLOWED_ROLES.MODERATOR, ALLOWED_ROLES.ADMIN, ALLOWED_ROLES.DEV];
      case '/home':
      default:
        return [ALLOWED_ROLES.SUPPORT, ALLOWED_ROLES.MODERATOR, ALLOWED_ROLES.ADMIN, ALLOWED_ROLES.DEV];
    }
  };

  if (loading && !publicRaffle) {
    return (
      <div className="loader-container">
        <div className="app-loader"></div>
        <div className="loading-text">{t('common.loading', { defaultValue: 'Загрузка приложения...' })}</div>
      </div>
    );
  }

  if ((!isLoggedIn || !user) && !publicRaffle) {
    return <Login onLogin={handleLogin} error={error} allowedRoles={ALLOWED_ROLES} />;
  }

  // Страница розыгрыша рендерится отдельно — без Navbar/Layout админки.
  // Доступ только для админа; остальные роли молча уводятся на /home.
  if (isRaffleRoute) {
    const isAdmin = user?.role === ALLOWED_ROLES.ADMIN || user?.role === ALLOWED_ROLES.DEV;
    return (
      <div className="rf-route-root">
        <ScrollToTopOnRouteChange />
        <Routes>
          <Route
            path="/raffle"
            element={
              isAdmin || RAFFLE_PUBLIC_ACCESS ? <Raffle /> : <Navigate to="/home" replace />
            }
          />
        </Routes>
      </div>
    );
  }

  // ChatProvider живёт выше вкладок: одно WebSocket-соединение на всё приложение,
  // сообщения приходят и когда вкладка чата закрыта.
  return (
    <ChatProvider>
    <div className="app-layout">
      <ScrollToTopOnRouteChange />
      <Sidebar
        user={user}
        onLogout={handleLogout}
        allowedRoles={ALLOWED_ROLES}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={() => setSidebarCollapsed((v) => !v)}
      />
      <div className={`app-body${sidebarCollapsed ? ' app-body--collapsed' : ''}`}>
      <Topbar
        user={user}
        onOpenSidebar={() => setSidebarOpen(true)}
        onToggleCollapsed={() => setSidebarCollapsed((v) => !v)}
      />
      <Layout>
        <Routes>
          <Route
            path="/home"
            element={
              <ProtectedRoute userRole={user?.role} requiredRoles={getRequiredRoles('/home')}>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logs"
            element={
              <ProtectedRoute userRole={user?.role} requiredRoles={getRequiredRoles('/logs')}>
                <Home />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/orders" 
            element={
              <ProtectedRoute userRole={user?.role} requiredRoles={getRequiredRoles('/orders')}>
                <Orders />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/orders/:orderId"
            element={
              <ProtectedRoute userRole={user?.role} requiredRoles={getRequiredRoles('/orders/:orderId')}>
                <OrderDetails />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/complaints" 
            element={
              <ProtectedRoute userRole={user?.role} requiredRoles={getRequiredRoles('/complaints')}>
                <Complaints />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/complaints/:complaintId" 
            element={
              <ProtectedRoute userRole={user?.role} requiredRoles={getRequiredRoles('/complaints/:complaintId')}>
                <ComplaintDetails />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/users" 
            element={
              <ProtectedRoute userRole={user?.role} requiredRoles={getRequiredRoles('/users')}>
                <Users />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/users/:userId" 
            element={
              <ProtectedRoute userRole={user?.role} requiredRoles={getRequiredRoles('/users/:userId')}>
                <UserDetails />
              </ProtectedRoute>
            } 
          />
            <Route
              path="/chat"
              element={
                <ProtectedRoute userRole={user?.role} requiredRoles={getRequiredRoles('/chat')}>
                  <ChatPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/statistics"
              element={
                <ProtectedRoute userRole={user?.role} requiredRoles={getRequiredRoles('/statistics')}>
                  <StatisticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/calendar"
              element={
                <ProtectedRoute userRole={user?.role} requiredRoles={getRequiredRoles('/calendar')}>
                  <CalendarPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute userRole={user?.role} requiredRoles={getRequiredRoles('/admin')}>
                  <Admin />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/notifications" 
              element={<Navigate to="/admin?tab=push" replace />} 
            />
            <Route
              path="/admin/promo-codes/:promoId"
              element={
                <ProtectedRoute userRole={user?.role} requiredRoles={getRequiredRoles('/admin/promo-codes/:promoId')}>
                  <AdminPromoCodeDetails />
                </ProtectedRoute>
              }
            />
          
          {/* Реддиректы */}
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="/login" element={<Navigate to="/home" replace />} />
          
          {/* 404 */}
          <Route path="*" element={
            <div className="flex items-center justify-center min-h-screen">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                <p className="text-xl text-gray-600">{t('common.notFound')}</p>
              </div>
            </div>
          } />
        </Routes>
      </Layout>
      </div>
    </div>
    </ChatProvider>
  );
};

const App = () => {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <AppContent />
    </Router>
  );
};

export default App;